import React from 'react';
import { get } from 'lodash';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { buildContextVehicleFromMakeModelSubmodelYear, updateVehicle } from 'client/actions/vehicle';
import { ModelPreloader } from 'client/data/luckdragon/redux/model-preloader';
import { buildLongTermRoadTestPath, LongTermRoadTestModel } from 'client/data/models/long-term-road-test';
import { buildVehiclePath, VehicleModel } from 'client/data/models/vehicle-v2';
import { getModelState } from 'client/data/luckdragon/model';
import { handle404Error } from 'site-modules/shared/utils/errors';
import { getParamsWithMakes } from 'client/utils/router-params';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { setAdsListToPreload } from 'site-modules/shared/components/native-ad/utils/utils';
import { isUsed } from 'site-modules/shared/utils/publication-states';
import { buildLtrtBreadcrumbs } from 'site-modules/long-term-road-test/utils/road-tests-breadcrumbs-builder';
import { INCENTIVES_AD, BUILD_PRICE_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { EdmundsNewCarsNoAdhesion } from 'site-modules/shared/pages/decorators/edmunds-new-cars-no-adhesion';
import { LtrtArticles } from './long-term-road-test';

export const pageOptions = {
  name: 'long_term_road_test',
  category: 'long_term_road_test',
  preload: (store, props) => {
    const params = getParamsWithMakes(props);
    const preloader = new ModelPreloader(store);

    preloader.resolve(buildVehiclePath(params), VehicleModel);
    preloader.resolve(buildLongTermRoadTestPath(params), LongTermRoadTestModel);

    return preloader.load().then(modelState => {
      const vehicle = modelState.get(buildVehiclePath(params), VehicleModel);
      const longTermRoadTestData = modelState.get(buildLongTermRoadTestPath(params), LongTermRoadTestModel);

      if (!longTermRoadTestData) {
        throw handle404Error({
          errorMessage: `There is no long-term-road-test content for ${params.year} ${params.make} ${params.model}`,
        });
      }
      const ltrtVehicle = vehicle || longTermRoadTestData.vehicle;
      if (!ltrtVehicle) {
        throw handle404Error({
          errorMessage: `There is no vehicle data for ${params.year} ${params.make} ${params.model}`,
        });
      }

      const contextVehicle = buildContextVehicleFromMakeModelSubmodelYear(ltrtVehicle, {
        isUsed: isUsed(ltrtVehicle.pubStates),
      });
      store.dispatch(updateVehicle(contextVehicle));

      setAdsListToPreload(preloader, store.getState(), [INCENTIVES_AD.AD_NAME, BUILD_PRICE_AD.AD_NAME], contextVehicle);

      return preloader.load();
    });
  },
  component: props => <LtrtArticles params={getParamsWithMakes(props)} />,
  decorator: EdmundsNewCarsNoAdhesion,
  seo: (state, props) => {
    const params = getParamsWithMakes(props);
    const modelState = getModelState(state);
    const pathname = get(props, 'location.pathname');
    const longTermRoadTestData = modelState.get(buildLongTermRoadTestPath(params), LongTermRoadTestModel);
    const vehicle = modelState.get(buildVehiclePath(params), VehicleModel);
    const ltrtVehicle = vehicle || longTermRoadTestData.vehicle;

    return {
      title: get(longTermRoadTestData, 'dataPoints.browserTitle', null),
      description: `Learn what it’s like to own the ${buildDisplayVehicle({
        makeModelSubmodelYear: ltrtVehicle,
        params,
      })}. View Edmunds' Long Term Road Test to get in-depth information on fuel economy, maintenance costs, performance, comfort and more at Edmunds.com.`,
      type: 'article',
      publishedTime: longTermRoadTestData.published,
      modifiedTime: longTermRoadTestData.updated,
      jsonld: [
        getBreadcrumbSchema(
          buildLtrtBreadcrumbs({
            pathname,
            vehicle: ltrtVehicle,
            hasSubmodel: !!params.submodel,
            hasVehicleData: !!vehicle,
          })
        ),
      ],
    };
  },
  options: {
    hasVehicleContext: true,
  },
};

export default pageDefinition(pageOptions);
